import { c as _c } from "react/compiler-runtime";
import { Button } from '@systemeio/components';
import { MagicWand01Icon } from '@systemeio/icons';
import { EditorExtensionEnum, emailTextExtensions, TipTapEmailEditor } from '@systemeio/tip-tap';
import EmailTemplateSelectorNewEditorModal from 'modules/email/components/email-template-selector/email-template-selector-modal';
import { MailingId } from 'modules/mailing/types/mailing-interface';
import { useEffect, useState } from 'react';
import { FormInput, Hint, SafeHtmlText } from 'shared/components';
import Confirmation from 'shared/components/confirmation-modal';
import { SubstituteInterface } from 'shared/components/dashboard-mail-editor/components/substitutes-block';
import { MailingPoolStatusHelpMessages, MailingPoolStatusHintTriggerClasses } from 'shared/components/dashboard-mail-editor/enums/mailing-pool-status-enum';
import { useEmailOptions } from 'shared/components/dashboard-mail-editor/hooks/use-email-options';
import { getEmailStatusLink } from 'shared/components/dashboard-mail-editor/utils/get-email-status-link';
import { isWithRecipient } from 'shared/components/dashboard-mail-editor/utils/is-with-recipients';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { SectionMessage } from 'shared/components/section-message';
import TransLoco from 'shared/components/trans-loco';
import { CARD_FRAME_CLASSNAME } from 'shared/constants/card-class-names';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { twMerge } from 'tailwind-merge';
import { useEmailFooterData } from '../../hooks/use-email-footer-data';
import { useSubstitutes } from '../../hooks/use-substitutes';
import { DashboardMailEditorCommonProps, ExtendedState } from '../../types/common';
import { NewEditor } from './new-editor';
export type EditorBlockProps<T extends ExtendedState> = Pick<DashboardMailEditorCommonProps<T>, 'isPreFetching' | 'errors' | 'tempState' | 'onChangeEmail' | 'disabled'> & {
  withRecipient?: boolean;
} & {
  isSwitchEditorButtonShown?: boolean;
  isEditorEditable?: boolean;
  showSettingsLink?: boolean;
  mailingId?: MailingId;
  additionalSubstitutes?: SubstituteInterface[];
  className?: string;
};
const EditorBlock = t0 => {
  const $ = _c(104);
  const {
    disabled,
    errors,
    isEditorEditable: t1,
    isPreFetching,
    isSwitchEditorButtonShown: t2,
    onChangeEmail,
    showSettingsLink: t3,
    tempState,
    withRecipient,
    mailingId,
    additionalSubstitutes,
    className
  } = t0;
  const isEditorEditable = t1 === undefined ? true : t1;
  const isSwitchEditorButtonShown = t2 === undefined ? false : t2;
  const showSettingsLink = t3 === undefined ? true : t3;
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const {
    data: options
  } = useEmailOptions();
  const [isTemplateModalOpened, setIsTemplateModalOpened] = useState(false);
  const [isConfirmationModalOpened, setIsConfirmationModalOpened] = useState(false);
  let t4;
  if ($[0] !== onChangeEmail || $[1] !== tempState) {
    t4 = async templateId => {
      tempState && (await onChangeEmail({
        ...tempState,
        useNewEditor: true,
        template: templateId
      }));
      setIsTemplateModalOpened(false);
    };
    $[0] = onChangeEmail;
    $[1] = tempState;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const onChooseTemplateModal = t4;
  let t5;
  if ($[3] !== onChangeEmail || $[4] !== tempState) {
    t5 = async () => {
      tempState && (await onChangeEmail({
        ...tempState,
        useNewEditor: !tempState.useNewEditor
      }));
      setIsConfirmationModalOpened(false);
    };
    $[3] = onChangeEmail;
    $[4] = tempState;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const onConfirmationModalOpened = t5;
  const {
    substitutionData
  } = useSubstitutes(additionalSubstitutes);
  const [emailFooterDataContent, setEmailFooterDataContent] = useState();
  const {
    emailFooterData
  } = useEmailFooterData();
  let t6;
  if ($[6] !== emailFooterData || $[7] !== tempState) {
    t6 = () => {
      if (tempState?.fromEmail) {
        const domain = tempState.fromEmail.split("@")[1];
        if (emailFooterData) {
          setEmailFooterDataContent(emailFooterData.items.find(item => item.domainName === domain)?.content);
        }
      }
    };
    $[6] = emailFooterData;
    $[7] = tempState;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = tempState?.fromEmail;
  let t8;
  if ($[9] !== emailFooterData || $[10] !== t7) {
    t8 = [t7, emailFooterData];
    $[9] = emailFooterData;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  useEffect(t6, t8);
  const t9 = tempState?.useNewEditor && "desktop:h-fit h-200";
  let t10;
  if ($[12] !== className || $[13] !== t9) {
    t10 = twMerge(CARD_FRAME_CLASSNAME, "flex w-full flex-col gap-4 p-5", t9, className);
    $[12] = className;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== disabled || $[16] !== errors.recipient || $[17] !== isPreFetching || $[18] !== onChangeEmail || $[19] !== t || $[20] !== tempState || $[21] !== withRecipient) {
    t11 = withRecipient && isWithRecipient(withRecipient, tempState) && <FormInput disabled={disabled} isPreFetching={isPreFetching} label={t("dashboard.email.recipient_address")} required={true} value={tempState?.recipient || ""} error={errors.recipient} onChange={e => tempState && onChangeEmail({
      ...(tempState as T),
      recipient: e.target.value
    })} />;
    $[15] = disabled;
    $[16] = errors.recipient;
    $[17] = isPreFetching;
    $[18] = onChangeEmail;
    $[19] = t;
    $[20] = tempState;
    $[21] = withRecipient;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== options || $[24] !== t || $[25] !== user) {
    t12 = user && options && <Hint ariaLabel={t("dashboard.email.subject")} triggerClassName={MailingPoolStatusHintTriggerClasses[options.emailPool]} label={<TransLoco t={t} i18nKey={MailingPoolStatusHelpMessages[options.emailPool]} components={{
      a: <a href={getEmailStatusLink(user?.dashboardLocale)} target="_blank" rel="noreferrer" className="link" />
    }} />}>{t("dashboard.email.subject")}</Hint>;
    $[23] = options;
    $[24] = t;
    $[25] = user;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = tempState?.subject || "";
  let t14;
  if ($[27] !== onChangeEmail || $[28] !== tempState) {
    t14 = e_0 => tempState && onChangeEmail({
      ...tempState,
      subject: e_0.target.value
    });
    $[27] = onChangeEmail;
    $[28] = tempState;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== disabled || $[31] !== errors.subject || $[32] !== isPreFetching || $[33] !== t12 || $[34] !== t13 || $[35] !== t14) {
    t15 = <FormInput disabled={disabled} isPreFetching={isPreFetching} label={t12} required={true} value={t13} onChange={t14} error={errors.subject} />;
    $[30] = disabled;
    $[31] = errors.subject;
    $[32] = isPreFetching;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== options || $[38] !== t || $[39] !== user) {
    t16 = user && options && <Hint label={t("dashboard.email.preview_text_tooltip")}>{t("dashboard.email.preview_text_label")}</Hint>;
    $[37] = options;
    $[38] = t;
    $[39] = user;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  const t17 = tempState?.previewText || "";
  let t18;
  if ($[41] !== onChangeEmail || $[42] !== tempState) {
    t18 = e_1 => tempState && onChangeEmail({
      ...tempState,
      previewText: e_1.target.value
    });
    $[41] = onChangeEmail;
    $[42] = tempState;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== disabled || $[45] !== errors.previewText || $[46] !== isPreFetching || $[47] !== t16 || $[48] !== t17 || $[49] !== t18) {
    t19 = <FormInput disabled={disabled} isPreFetching={isPreFetching} label={t16} value={t17} error={errors.previewText} onChange={t18} />;
    $[44] = disabled;
    $[45] = errors.previewText;
    $[46] = isPreFetching;
    $[47] = t16;
    $[48] = t17;
    $[49] = t18;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== t) {
    t20 = t("dashboard.email.body");
    $[51] = t;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== t20) {
    t21 = <span className="text-text-secondary text-base">{t20}</span>;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== disabled || $[56] !== isPreFetching || $[57] !== isSwitchEditorButtonShown || $[58] !== t || $[59] !== tempState) {
    t22 = isSwitchEditorButtonShown && !isPreFetching && <><Button disabled={disabled} onClick={() => {
        if (tempState) {
          if (!tempState.useNewEditor && !tempState.html) {
            !tempState.useNewEditor && setIsTemplateModalOpened(true);
          } else {
            setIsConfirmationModalOpened(true);
          }
        }
      }} variant="link"><MagicWand01Icon />{tempState?.useNewEditor ? t("dashboard.email.switch_to_the_classic_editor") : t("dashboard.email.switch_to_the_visual_editor")}</Button></>;
    $[55] = disabled;
    $[56] = isPreFetching;
    $[57] = isSwitchEditorButtonShown;
    $[58] = t;
    $[59] = tempState;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== t21 || $[62] !== t22) {
    t23 = <div className="flex items-center justify-between gap-3">{t21}{t22}</div>;
    $[61] = t21;
    $[62] = t22;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  let t24;
  if ($[64] !== disabled || $[65] !== emailFooterDataContent || $[66] !== errors.common || $[67] !== isEditorEditable || $[68] !== isPreFetching || $[69] !== mailingId || $[70] !== onChangeEmail || $[71] !== substitutionData || $[72] !== tempState) {
    t24 = <div className="relative h-full">{tempState?.useNewEditor && mailingId ? <NewEditor tempState={tempState} isPreFetching={isPreFetching} mailingId={mailingId} disabled={disabled || !isEditorEditable} /> : <div><TipTapEmailEditor extensions={emailTextExtensions} isPreFetching={isPreFetching} isEditable={!disabled && isEditorEditable} content={tempState?.body || undefined} onChange={data => tempState && onChangeEmail({
          ...tempState,
          body: data
        })} extensionOptions={{
          [EditorExtensionEnum.Substitution]: {
            substitutions: substitutionData
          }
        }} className={twMerge(emailFooterDataContent && "rounded-b-none", errors.common.length !== 0 && "border-field-border-error")} />{emailFooterDataContent && <div className="border-border-200 rounded-b-lg border border-t-0 p-2"><SafeHtmlText as="div" html={emailFooterDataContent.replace(/\n/g, "<br />")} /></div>}</div>}</div>;
    $[64] = disabled;
    $[65] = emailFooterDataContent;
    $[66] = errors.common;
    $[67] = isEditorEditable;
    $[68] = isPreFetching;
    $[69] = mailingId;
    $[70] = onChangeEmail;
    $[71] = substitutionData;
    $[72] = tempState;
    $[73] = t24;
  } else {
    t24 = $[73];
  }
  let t25;
  if ($[74] !== t23 || $[75] !== t24) {
    t25 = <div className="flex basis-full flex-col gap-2">{t23}{t24}</div>;
    $[74] = t23;
    $[75] = t24;
    $[76] = t25;
  } else {
    t25 = $[76];
  }
  let t26;
  if ($[77] !== showSettingsLink || $[78] !== t) {
    t26 = showSettingsLink && <SectionMessage><TransLoco t={t} i18nKey="dashboard.email.change_footer_link" components={{
        a: <LinkWithoutPrefetch href="/profile/mailing-settings" className="link" />
      }} /></SectionMessage>;
    $[77] = showSettingsLink;
    $[78] = t;
    $[79] = t26;
  } else {
    t26 = $[79];
  }
  let t27;
  if ($[80] !== errors.body) {
    t27 = errors.body && <p className="text-text-error text-sm">{errors.body}</p>;
    $[80] = errors.body;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  let t28;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => setIsConfirmationModalOpened(false);
    $[82] = t28;
  } else {
    t28 = $[82];
  }
  let t29;
  if ($[83] !== t || $[84] !== tempState?.useNewEditor) {
    t29 = tempState?.useNewEditor ? t("dashboard.email.switch_to_the_classic_editor_message") : t("dashboard.email.switch_to_the_visual_editor_message");
    $[83] = t;
    $[84] = tempState?.useNewEditor;
    $[85] = t29;
  } else {
    t29 = $[85];
  }
  let t30;
  if ($[86] !== isConfirmationModalOpened || $[87] !== onConfirmationModalOpened || $[88] !== t29) {
    t30 = <Confirmation opened={isConfirmationModalOpened} onClose={t28} onConfirm={onConfirmationModalOpened} confirmationText={t29} />;
    $[86] = isConfirmationModalOpened;
    $[87] = onConfirmationModalOpened;
    $[88] = t29;
    $[89] = t30;
  } else {
    t30 = $[89];
  }
  let t31;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = () => setIsTemplateModalOpened(false);
    $[90] = t31;
  } else {
    t31 = $[90];
  }
  let t32;
  if ($[91] !== isTemplateModalOpened || $[92] !== onChooseTemplateModal) {
    t32 = <EmailTemplateSelectorNewEditorModal opened={isTemplateModalOpened} onClose={t31} onChoose={onChooseTemplateModal} />;
    $[91] = isTemplateModalOpened;
    $[92] = onChooseTemplateModal;
    $[93] = t32;
  } else {
    t32 = $[93];
  }
  let t33;
  if ($[94] !== t10 || $[95] !== t11 || $[96] !== t15 || $[97] !== t19 || $[98] !== t25 || $[99] !== t26 || $[100] !== t27 || $[101] !== t30 || $[102] !== t32) {
    t33 = <div className={t10}>{t11}{t15}{t19}{t25}{t26}{t27}{t30}{t32}</div>;
    $[94] = t10;
    $[95] = t11;
    $[96] = t15;
    $[97] = t19;
    $[98] = t25;
    $[99] = t26;
    $[100] = t27;
    $[101] = t30;
    $[102] = t32;
    $[103] = t33;
  } else {
    t33 = $[103];
  }
  return t33;
};
export default EditorBlock;